import type { SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | null;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
}

export default function Select({
  label,
  error,
  hint,
  options,
  placeholder,
  id,
  className = '',
  ...rest
}: SelectProps) {
  const selectId = id || rest.name || label?.replace(/\s+/g, '-').toLowerCase();

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={selectId}
          className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label"
        >
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        <select
          id={selectId}
          className={[
            'h-11 w-full cursor-pointer appearance-none rounded-md border bg-background-50 pl-3.5 pr-10 text-sm text-foreground-900',
            'transition-colors duration-200 focus:outline-none focus:ring-2',
            error
              ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
              : 'border-background-300 focus:border-primary-400 focus:ring-primary-200',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-3 flex h-4 w-4 items-center justify-center text-foreground-400">
          <i className="ri-arrow-down-s-line text-base"></i>
        </span>
      </div>
      {error ? (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
          <i className="ri-error-warning-line text-sm"></i>
          {error}
        </p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-foreground-500">{hint}</p>
      )}
    </div>
  );
}